import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  BarChart3,
  Ban,
  CheckCircle,
  ChevronRight,
  Gift,
  History,
  RefreshCw,
  RotateCcw,
  TrendingUp,
} from 'lucide-react'
import { useState, type ReactElement } from 'react'

import {
  EnsureStripeProductsDialog,
  formatEuroCents,
  overviewQuery,
  syncStatusQuery,
  type SubscriptionsKpiTotals,
  type SubscriptionsOverview,
  useEnsureStripeProducts,
} from '~/features/subscriptions'
import { formatRelative, initialsFromName } from '~/features/organizations/components/format'
import type { AppError } from '~/lib/result'

/**
 * `/admin/subscriptions` (index) — Overview surface.
 *
 * Header band + 6 KPI tiles + past-due triage card + recent admin
 * overrides. The "Ensure Stripe Products" primary in the header opens
 * the idempotent sync dialog.
 */
export const Route = createFileRoute('/admin/subscriptions/')({
  component: SubscriptionsOverviewPage,
})

function SubscriptionsOverviewPage(): ReactElement {
  const overview = useQuery(overviewQuery())
  const syncStatus = useQuery(syncStatusQuery())
  const ensureMutation = useEnsureStripeProducts()
  const [ensureOpen, setEnsureOpen] = useState(false)

  const apiError = overview.error as AppError | null
  if (apiError?.code === 'forbidden') {
    return <PermissionDenied />
  }

  const totals = overview.data?.totals ?? null
  const pastDueRows = overview.data?.past_due_rows ?? []
  const recentOverrides = overview.data?.recent_overrides ?? []

  const ensureStatus = syncStatus.data
    ? {
        localPlans: syncStatus.data.rows.length,
        stripePlans: syncStatus.data.rows.filter(
          (r) =>
            r.status === 'in_sync' ||
            r.status === 'out_of_sync' ||
            r.status === 'stripe_only',
        ).length,
        missingInStripe: syncStatus.data.rows.filter(
          (r) => r.status === 'local_only' || r.status === 'missing',
        ).length,
        mismatchedPrices: syncStatus.data.rows.filter(
          (r) => r.status === 'out_of_sync',
        ).length,
      }
    : null

  const pastDueCount = totals?.past_due ?? 0
  return (
    <div className="ka-page ka-subs-page">
      {pastDueCount > 0 ? (
        <div className="ka-page-banner is-destructive ka-subs-banner" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <div className="ka-page-banner-body">
            <strong>{pastDueCount} past-due subscriptions</strong>
            {' '}· open the triage table below
          </div>
          <button type="button" className="ka-btn ka-btn--secondary ka-btn--sm">
            Open triage
          </button>
        </div>
      ) : null}

      <SubscriptionsKpis totals={totals} isLoading={overview.isLoading} />

      <StatusDistribution totals={totals} />

      <PastDueTriage rows={pastDueRows} />

      <div className="ka-grid ka-grid-2 ka-subs-bottom-grid">
        <ConversionRibbon />
        <RecentOverrides rows={recentOverrides} />
      </div>

      <div className="ka-subs-dialog-actions">
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm">
          <Gift size={12} aria-hidden="true" /> Grant subscription
        </button>
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm">
          <Ban size={12} aria-hidden="true" /> Revoke
        </button>
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm">
          <RefreshCw size={12} aria-hidden="true" /> Change plan
        </button>
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm">
          <RotateCcw size={12} aria-hidden="true" /> Refund
        </button>
        <button
          type="button"
          className="ka-btn ka-btn--secondary ka-btn--sm"
          onClick={() => setEnsureOpen(true)}
        >
          <CheckCircle size={12} aria-hidden="true" /> Ensure Stripe products
        </button>
      </div>

      {ensureOpen ? (
        <EnsureStripeProductsDialog
          status={ensureStatus}
          result={ensureMutation.data ?? null}
          error={ensureMutation.error}
          isSubmitting={ensureMutation.isPending}
          onClose={() => {
            setEnsureOpen(false)
            ensureMutation.reset()
          }}
          onConfirm={() => ensureMutation.mutate()}
        />
      ) : null}
    </div>
  )
}

function SubscriptionsKpis({
  totals,
  isLoading,
}: {
  readonly totals: SubscriptionsKpiTotals | null
  readonly isLoading: boolean
}): ReactElement {
  // Delta (period-over-period growth) requires a historical series the
  // backend doesn't ship yet — hide the delta row entirely rather than
  // show fabricated `6.8%` / `16.6%` design-mockup numbers.
  const tiles = isLoading || !totals
    ? [
        { label: 'Active subs', value: '—' },
        { label: 'Trials', value: '—' },
        { label: 'Past Due', value: '—', tone: 'destructive' as const },
        { label: 'Canceled', value: '—' },
        { label: 'Refunds', value: '—' },
        { label: 'MRR', value: '—' },
      ]
    : [
        { label: 'Active subs', value: formatInt(totals.active) },
        { label: 'Trials', value: formatInt(totals.trial) },
        { label: 'Past Due', value: formatInt(totals.past_due), tone: 'destructive' as const },
        { label: 'Canceled', value: formatInt(totals.canceled) },
        { label: 'Refunds', value: formatInt(totals.refunds) },
        { label: 'MRR', value: formatEuroCents(totals.mrr_cents) },
      ]

  return (
    <div className="ka-kpi-strip ka-subs-kpi-strip" role="list" aria-label="Subscription KPIs">
      {tiles.map((tile) => (
        <div className="ka-kpi" key={tile.label} role="listitem">
          <span className="ka-kpi-label">{tile.label}</span>
          <span className={`ka-kpi-value${tile.tone === 'destructive' ? ' is-destructive' : ''}`}>
            {tile.value}
          </span>
        </div>
      ))}
    </div>
  )
}

function StatusDistribution({
  totals,
}: {
  readonly totals: SubscriptionsKpiTotals | null
}): ReactElement {
  const active = totals?.active ?? 0
  const trial = totals?.trial ?? 0
  const pastDue = totals?.past_due ?? 0
  const canceled = totals?.canceled ?? 0
  // The backend doesn't currently expose a `suspended` count separate
  // from `canceled`. Hide the segment until the contract grows the field
  // rather than render a fabricated number.
  const total = active + trial + pastDue + canceled

  return (
    <section className="ka-card ka-card--flush ka-page-section">
      <header className="ka-card-head">
        <div className="ka-card-head-icon" aria-hidden="true">
          <BarChart3 size={14} />
        </div>
        <div className="ka-card-head-title">Status distribution</div>
        <div className="ka-card-head-meta">{active} active subscriptions</div>
      </header>
      {total === 0 ? (
        <div className="ka-empty">
          <div className="ka-empty-title">No subscriptions yet</div>
        </div>
      ) : (
        <div className="ka-subs-status-body">
          <div className="ka-subs-status-bar" aria-hidden="true">
            {active > 0 ? (
              <div className="is-success" style={{ flex: active }}>Active {active}</div>
            ) : null}
            {trial > 0 ? (
              <div className="is-info" style={{ flex: trial }}>{trial}</div>
            ) : null}
            {pastDue > 0 ? (
              <div className="is-destructive" style={{ flex: pastDue }}>{pastDue}</div>
            ) : null}
            {canceled > 0 ? (
              <div className="is-warning" style={{ flex: canceled }}>{canceled}</div>
            ) : null}
          </div>
          <div className="ka-subs-status-legend">
            <Legend color="success" label={`Active ${active}`} />
            <Legend color="info" label={`Trial ${trial}`} />
            <Legend color="destructive" label={`Past Due ${pastDue}`} />
            <Legend color="warning" label={`Canceled ${canceled}`} />
          </div>
        </div>
      )}
    </section>
  )
}

function Legend({ color, label }: { readonly color: string; readonly label: string }): ReactElement {
  return (
    <span className="ka-subs-legend-item">
      <span className={`ka-subs-legend-dot is-${color}`} aria-hidden="true" />
      {label}
    </span>
  )
}

function PastDueTriage({
  rows,
}: {
  readonly rows: SubscriptionsOverview['past_due_rows']
}): ReactElement {
  const displayRows = rows

  return (
    <section className="ka-card ka-card--flush ka-page-section">
      <header className="ka-card-head">
        <div className="ka-card-head-icon is-destructive" aria-hidden="true">
          <AlertCircle size={14} />
        </div>
        <div className="ka-card-head-title">Past-due triage</div>
        <div className="ka-card-head-meta">{displayRows.length} subscriptions</div>
      </header>
      {displayRows.length === 0 ? (
        <div className="ka-empty">
          <div className="ka-empty-title">No past-due subscriptions</div>
        </div>
      ) : (
      <table className="ka-table ka-subs-triage-table">
        <thead>
          <tr>
            <th>Org</th>
            <th>Plan</th>
            <th className="ka-num">Failed</th>
            <th>Last attempt</th>
            <th className="ka-num">Amount</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="ka-cell-flex">
                  <span className="ka-tile ka-subs-org-tile" aria-hidden="true">
                    {initialsFromName(row.org_name)}
                  </span>
                  <span>{row.org_name}</span>
                </div>
              </td>
              <td>
                <span className={`ka-chip ${row.plan_name === 'Enterprise' ? 'ka-chip--info' : 'ka-chip--success'}`}>
                  {row.plan_name}
                </span>
              </td>
              <td className="ka-num">
                <span className="ka-chip ka-chip--destructive">{row.failed_attempts}</span>
              </td>
              <td className="ka-td-meta">
                {row.last_attempt_at ? formatRelative(row.last_attempt_at) : '—'}
              </td>
              <td className="ka-num">{formatEuroCents(row.amount_cents)}</td>
              <td>
                <div className="ka-subs-row-actions">
                  <button type="button" className="ka-btn ka-btn--secondary ka-btn--sm">
                    <RefreshCw size={12} aria-hidden="true" /> Retry
                  </button>
                  <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm">
                    Pause
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
    </section>
  )
}

function ConversionRibbon(): ReactElement {
  // Real conversion metrics (trial→paid, ARPU) require a cohort backend
  // that doesn't exist yet. Show a placeholder rather than design-mockup
  // values (`38% / 4.2% / 24% / €113`) that misled admins.
  const stats = [
    { label: 'Trial → Paid', value: '—' },
    { label: 'Free → Paid', value: '—' },
    { label: 'Annual ratio', value: '—' },
    { label: 'Avg ARPU', value: '—' },
  ]

  return (
    <section className="ka-card">
      <header className="ka-card-head ka-card-head--no-border">
        <div className="ka-card-head-icon" aria-hidden="true">
          <TrendingUp size={14} />
        </div>
        <div className="ka-card-head-title">Conversion ribbon</div>
      </header>
      <div className="ka-stat2x2">
        {stats.map((stat) => (
          <div className="ka-stat2x2-cell" key={stat.label}>
            <div className="ka-stat2x2-label">{stat.label}</div>
            <div className="ka-stat2x2-value">{stat.value}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

function RecentOverrides({
  rows,
}: {
  readonly rows: SubscriptionsOverview['recent_overrides']
}): ReactElement {
  const displayRows = rows.slice(0, 5)

  return (
    <section className="ka-card ka-card--flush">
      <header className="ka-card-head">
        <div className="ka-card-head-icon" aria-hidden="true">
          <History size={14} />
        </div>
        <div className="ka-card-head-title">Recent admin overrides</div>
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm ka-card-head-action">
          Open audit log <ChevronRight size={12} aria-hidden="true" />
        </button>
      </header>
      {displayRows.length === 0 ? (
        <div className="ka-empty">
          <div className="ka-empty-title">No recent admin overrides</div>
        </div>
      ) : (
      <div className="ka-rowlist">
        {displayRows.map((entry) => (
          <div className="ka-activity-row" key={entry.id}>
            <div className="ka-activity-avatar" aria-hidden="true">
              {(entry.actor_name ?? 'system').slice(0, 2).toUpperCase()}
            </div>
            <div className="ka-activity-body">
              <div className="ka-activity-line">
                <strong>{entry.actor_name ?? 'system'}</strong>{' '}
                <span className={`ka-chip ${overrideChipClass(entry.verb)}`}>
                  {entry.verb}
                </span>{' '}
                <span className="ka-text-muted">· {entry.target_label ?? 'Subscription'}</span>
              </div>
              <div className="ka-activity-meta">{formatRelative(entry.occurred_at)}</div>
            </div>
          </div>
        ))}
      </div>
      )}
    </section>
  )
}

function overrideChipClass(verb: string): string {
  const normalized = verb.toLowerCase()
  if (normalized.includes('refund')) return 'ka-chip--warning'
  if (normalized.includes('revoke')) return 'ka-chip--destructive'
  if (normalized.includes('change')) return 'ka-chip--info'
  return 'ka-chip--success'
}

function formatInt(value: number): string {
  return value.toLocaleString('en-IE')
}

function PermissionDenied(): ReactElement {
  return (
    <div className="ka-card ka-page-section">
      <div className="ka-empty">
        <div className="ka-empty-title">Restricted</div>
        <div className="ka-empty-body">
          You don&rsquo;t have the <code>subscription_management</code>{' '}
          permission. Request access from a super-admin to use this surface.
        </div>
      </div>
    </div>
  )
}
